import React, { useEffect, useMemo, useState } from 'react';

import PLannerWeekGroupWrapper from './style';
import { getPlannerWeekDayLabel } from 'helpers/calendarHelper';
import { mainCalendarPrefDayVar } from 'utils/cache/calendar';
import { FIRST_WEEK_DAY, LAST_WEEK_DAY } from 'utils/constants/planner';

import Group from '../group/group';
import DraggableWeekGroup from './draggableWeekGroup';
import { createPortal } from 'react-dom';
import { useDisableBodyScroll } from 'hooks/disableScrollHook';
import { IPlannerGroup, calendarGroup } from 'model/calendar/groups';
import { useReactiveVar } from '@apollo/client';
import { useLazyQuery } from '@apollo/react-hooks';
import { GET_PROVIDER_WITH_AVAILABILITY } from 'api/graphql/v2/queries/AvailabilityTemplate';
import {
  getClinicsAbbreviation,
  mapDayAvailabilityToCalendarEvent
} from 'utils/mappers/availabilityTemplate';
import { IWeeklySessionsData } from 'model/v2/providerCapacity';

interface Props {
  group: IPlannerGroup;
  setRemovedGroups: React.Dispatch<React.SetStateAction<number[]>>;
  removedGroups: number[];
  handleAutoFetching: () => void;
  events: any;
}

const PlannerWeekGroup: React.FC<Props> = ({
  group,
  setRemovedGroups,
  removedGroups,
  handleAutoFetching
}: Props) => {
  const calendarDate = useReactiveVar(mainCalendarPrefDayVar);
  const [isGroupDragged, setIsGroupDragged] = useState<boolean>(false);
  const [isShown, setIsShown] = useState<boolean>(false);
  const [availabilityEvents, setAvailabilityEvents] = useState();
  const [clinics, setClinics] = useState();
  const [getProviderWithAvailability, { data }] = useLazyQuery(
    GET_PROVIDER_WITH_AVAILABILITY,
    {
      fetchPolicy: 'no-cache',
      onCompleted: () => {
        setAvailabilityEvents(
          data?.providerWithAvailability?.availability?.dayAvailabilities!
        );
        setClinics(data?.clinicWithItsHub);
      }
    }
  );
  
  // Debug logging for subscription updates
  useEffect(() => {
    if (group.speciality?.department === 'DT' && group.id?.split(':')[1] === '0') {
      console.log('[PlannerWeekGroup] DT Provider group updated:', {
        id: group.id,
        baseId: group.baseId,
        bookedSessions: group.providerCapacity?.providerBookedSessions,
        targetSessions: group.providerCapacity?.providerTargetSessions
      });
    }
  }, [group]);
  
  useDisableBodyScroll(isGroupDragged);
  const label = useMemo(() => {
    return group.id === 'DEFAULT'
      ? ''
      : getPlannerWeekDayLabel(
          (group.id?.split(':')[1] as unknown) as number,
          calendarDate
        );
  }, [calendarDate, group.id]);
  const indexOfDay = group.id?.split(':')[1];
  const showFullGroup = useMemo(() => {
    return group.id?.split(':')[1] === FIRST_WEEK_DAY || false;
  }, [group]);
  const providerId = group.id?.split(':')[0];
  const attendeeTypeGroup = group.attendeeType;
  const isDTProvider =
    group.attendeeType === calendarGroup.provider &&
    group.speciality?.department === 'DT';

  const getDaySessionsLabel = (dayIndex: number) => {
    if (!isDTProvider) return null;
    const bookedSessions = group.providerCapacity
      ?.providerBookedSessions as IWeeklySessionsData | undefined;

    const dayMap: { [key: number]: keyof IWeeklySessionsData } = {
      0: 'sun',
      1: 'mon',
      2: 'tues',
      3: 'wed',
      4: 'thurs',
      5: 'fri',
      6: 'sat'
    };

    const dayKey = dayMap[dayIndex];
    if (!dayKey) return null;

    const booked = (bookedSessions?.[dayKey] as number) ?? 0;

    if (dayIndex < 1 || dayIndex > 5) return null;

    // AXON-3559: The per-day target is no longer meaningful (limits are weekly-only),
    // so display the booked session count alone.
    return {
      label: `${booked} ${booked === 1 ? 'session' : 'sessions'}`,
      color: 'rgb(65, 87, 102)'
    };
  };

  useEffect(() => {
    getProviderWithAvailability({
      variables: {
        id: Number(providerId),
        date: calendarDate
      }
    });
  }, [providerId, calendarDate, getProviderWithAvailability]);
  const dayOfAvailabilites = mapDayAvailabilityToCalendarEvent(
    availabilityEvents!,
    clinics
  );
  const clinicAbbreviation = getClinicsAbbreviation(
    dayOfAvailabilites,
    clinics
  );
  let clinicAbbreviationName: string[];

  const getClinicsAbbreviationName = (attendeeTypeGroup: string) => {
    const dayIdx = indexOfDay ? parseInt(indexOfDay) : undefined;
    const sessionsData = dayIdx !== undefined ? getDaySessionsLabel(dayIdx) : null;

    if (
      indexOfDay &&
      indexOfDay !== undefined &&
      attendeeTypeGroup === 'provider'
    ) {
      clinicAbbreviationName = clinicAbbreviation[parseInt(indexOfDay)];
    }
    if (
      clinicAbbreviation !== undefined &&
      clinicAbbreviationName?.length == 1
    ) {
      return (
        <>
          <p className="day-label-with-clinics">{label}</p>
          <p className="clinic-label">{clinicAbbreviationName}</p>
          {sessionsData && (
            <p 
              className="sessions-label"
              style={{
                color: sessionsData.color
              }}
            >
              {sessionsData.label}
            </p>
          )}
        </>
      );
    } else if (
      clinicAbbreviation !== undefined &&
      clinicAbbreviationName?.length == 2
    ) {
      return (
        <>
          <p className="day-label-with-clinics">{label}</p>
          <p className="clinic-label">
            {clinicAbbreviationName[0] + '|' + clinicAbbreviationName[1]}
          </p>
          {sessionsData && (
            <p 
              className="sessions-label"
              style={{
                color: sessionsData.color
              }}
            >
              {sessionsData.label}
            </p>
          )}
        </>
      );
    } else if (
      clinicAbbreviation !== undefined &&
      clinicAbbreviationName?.length == 3
    ) {
      return (
        <>
          <p className="day-label-with-clinics">{label}</p>
          <p className="clinic-label">
            {clinicAbbreviationName[0] +
              '|' +
              clinicAbbreviationName[1] +
              '|' +
              clinicAbbreviationName[2]}
          </p>
          {sessionsData && (
            <p 
              className="sessions-label"
              style={{
                color: sessionsData.color
              }}
            >
              {sessionsData.label}
            </p>
          )}
        </>
      );
    } else if (
      clinicAbbreviation !== undefined &&
      clinicAbbreviationName?.length == 4
    ) {
      return (
        <>
          <p className="day-label-with-clinics">{label}</p>
          <p className="clinic-label-font-adjusted">
            {clinicAbbreviationName[0] +
              '|' +
              clinicAbbreviationName[1] +
              '|' +
              clinicAbbreviationName[2] +
              '|' +
              clinicAbbreviationName[3]}
          </p>
          {sessionsData && (
            <p 
              className="sessions-label"
              style={{
                color: sessionsData.color
              }}
            >
              {sessionsData.label}
            </p>
          )}
        </>
      );
    } else if (
      clinicAbbreviation !== undefined &&
      clinicAbbreviationName?.length == 5
    ) {
      return (
        <>
          <p className="day-label-with-clinics">{label}</p>
          <p className="clinic-label-font-adjusted">
            {clinicAbbreviationName[0] +
              '|' +
              clinicAbbreviationName[1] +
              '|' +
              clinicAbbreviationName[2] +
              '|' +
              clinicAbbreviationName[3] +
              '|' +
              clinicAbbreviationName[4]}
          </p>
          {sessionsData && (
            <p 
              className="sessions-label"
              style={{
                color: sessionsData.color
              }}
            >
              {sessionsData.label}
            </p>
          )}
        </>
      );
    } else if (
      clinicAbbreviation !== undefined &&
      clinicAbbreviationName?.length == 0
    ) {
      return (
        <>
          <p className="day-label-with-clinics">{label}</p>
          {sessionsData && (
            <p 
              className="sessions-label"
              style={{
                color: sessionsData.color
              }}
            >
              {sessionsData.label}
            </p>
          )}
        </>
      );
    } else {
      return (
        <>
          <p className="day-label-with-clinics">{label}</p>
          {sessionsData && (
            <p 
              className="sessions-label"
              style={{
                color: sessionsData.color
              }}
            >
              {sessionsData.label}
            </p>
          )}
        </>
      );
    }
  };
  const ClientProviderDiffHandler = (attendeeTypeGroup: string) => {
    if (attendeeTypeGroup === 'provider') return true;
    else return false;
  };
  const WeekGroupLabel = useMemo(
    () =>
      ClientProviderDiffHandler(attendeeTypeGroup) ? (
        <div className="day-label-wrapper day-provider">
          <p>{getClinicsAbbreviationName(attendeeTypeGroup)}</p>
        </div>
      ) : (
        <div className="day-label-wrapper day-client">
          <p>{getClinicsAbbreviationName(attendeeTypeGroup)}</p>
        </div>
      ),
    [label, clinicAbbreviation, group.providerCapacity?.providerBookedSessions, group.providerCapacity?.providerTargetSessions]
  );

  const draggableWeekGroup = useMemo(() => {
    return (
      <DraggableWeekGroup
        setIsGroupDragged={setIsGroupDragged}
        isShown={isShown}
        isGroupDragged={isGroupDragged}
        group={group}
        setIsShown={setIsShown}
        setRemovedGroups={setRemovedGroups}
        removedGroups={removedGroups}
        showFullGroup={showFullGroup}
        handleAutoFetching={handleAutoFetching}
      />
    );
  }, [
    group,
    handleAutoFetching,
    isGroupDragged,
    isShown,
    removedGroups,
    setRemovedGroups,
    showFullGroup
  ]);

  return (
    <PLannerWeekGroupWrapper
      type={group?.attendeeType}
      // TODO: check that get in specialtiesMap take the id as number
      specialityColor={group?.speciality?.color}
      isLastDay={group?.id?.split(':')[1] === LAST_WEEK_DAY}
      isGroupDragged={isGroupDragged}
    >
      {/* render dragged group outside dom */}
      {showFullGroup && isGroupDragged ? (
        createPortal(
          <>
            {draggableWeekGroup}
            {WeekGroupLabel}
          </>,
          document.body
        )
      ) : showFullGroup ? (
        <>
          {draggableWeekGroup}
          {WeekGroupLabel}
        </>
      ) : (
        <>
          {/* render day label only */}

          <Group
            cloneGroup={false}
            isShown={isShown}
            group={group}
            setRemovedGroups={setRemovedGroups}
            removedGroups={removedGroups}
            showFullGroup={showFullGroup}
          />
          {WeekGroupLabel}
        </>
      )}
      {/* render placeholder */}
      {isGroupDragged && (
        <>
          <Group
            cloneGroup={true}
            isShown={isShown}
            group={group}
            setRemovedGroups={setRemovedGroups}
            removedGroups={removedGroups}
            showFullGroup={true}
          />
          {WeekGroupLabel}
        </>
      )}
    </PLannerWeekGroupWrapper>
  );
};

export default React.memo(PlannerWeekGroup, (prevProps, nextProps) => {
  // Custom comparison to detect providerCapacity changes
  const prevCapacity = prevProps.group.providerCapacity;
  const nextCapacity = nextProps.group.providerCapacity;
  
  // If providerCapacity changed, re-render
  if (
    prevCapacity?.providerBookedSessions !== nextCapacity?.providerBookedSessions ||
    prevCapacity?.providerTargetSessions !== nextCapacity?.providerTargetSessions
  ) {
    return false; // false means re-render
  }
  
  // Default shallow comparison for other props
  return (
    prevProps.group.id === nextProps.group.id &&
    prevProps.removedGroups === nextProps.removedGroups &&
    prevProps.events === nextProps.events
  );
});
